<template>
  <div class="informers-list">
    <div class="grid" :class="{
      'grid-skeleton': loading,
      'grid-three-or-more': informers.length > 3,
      'grid-less-than-three': informers.length > 0 && informers.length <= 3
    }">
      <div v-if="loading">
        <InformerCardSkeleton v-for="n in 10" :key="n" />
      </div>
      <draggable v-else-if="informers.length" v-model="informers" v-bind="dragOptions" @start="drag = true"
        @end="sortInformers()" tag="div">
        <transition-group>
          <InformerCard v-for="informer in informers"
          :key="informer.id"
          :informerInfo="informer"
          :editPermission="editPermission"
          :languages="languages"
          :isViewscreen="isViewscreen"
          :isMediaChecked="informersToViewscreenArray.some(item => item.id === informer.context.id)"
          :displays="displays"
          @addToMediaArray="addToMediaArray(informer.context.id)"
          @deleteFromMediaArray="deleteFromMediaArray(informer.context.id)"
          @openFullInfo="openInformerModal(informer)"
          @openEdit="openInformerPage(informer.context.id)" />
        </transition-group>
      </draggable>
      <EmptyState v-else :text="emptyStateText" :isAddDisabled="false" textBtn="Add"
        @add="createInformer" />
    </div>
    <a-modal v-model="isInformerModalOpen" :footer="null" width="1100px" :destroyOnClose="true">
      <template #title>
        <div>
          <span style="display: block;">Informer info</span>
          <span class="modal-uuid">UUID: {{ informerUUID }}</span>
        </div>
      </template>
      <InformerInfoModal :informerInfo="informerObject" :languages="languages" :displays="displays" @close="closeInformerModal" />
    </a-modal>
  </div>
</template>
<script>
import InformerCard from '@/views/apps/media/components/informers/informerCard.vue'
import InformerCardSkeleton from '@/views/apps/media/components/informers/informerCardSkeleton.vue'
import InformerInfoModal from '@/views/apps/media/components/informers/informerInfoModal.vue'
import EmptyState from '@/components/custom/emptyState.vue'

import MediaService from '@/services/api/apiMediaService'
// import ViewscreenService from '@/services/api/apiViewscreenService'

import { routerPushWithFilters } from '@/services/utils/routerHelpers'

import draggable from 'vuedraggable'
export default {
  name: 'Informers-list',
  components: {
    draggable,
    InformerCard,
    InformerCardSkeleton,
    InformerInfoModal,
    EmptyState,
  },
  props: {
    editPermission: {
      type: Boolean,
      default: false,
    },
    languages: {
      type: Array,
    },
    filters: {
      type: Object,
    },
    filtersReady: {
      type: Boolean,
      default: false,
    },
    isAddAllChecked: {
      type: Boolean,
      default: false,
    },
    isViewscreen: {
      type: Boolean,
      default: false,
    },
    mediaToViewscreenArray: {
      type: Array,
    },
    displays: {
      type: Object,
    },
  },
  data() {
    return {
      isInternalCheckboxChange: false,
      informers: [],
      drag: false,
      loading: false,
      apps: undefined,
      isInformerModalOpen: false,
      informerObject: undefined,
      informersToViewscreenArray: [],
    }
  },
  computed: {
    informerUUID() {
      return this.informerObject ? this.informerObject.context.uuid : ''
    },
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost',
      }
    },
    emptyStateText() {
      return this.filters.name?.length ? 'Oops! Nothing matches your search.' : 'Add first informer'
    },
    filtersToBeWatched() {
      return Object.assign({}, this.filters)
    },
  },
  async created() {
    await this.$nextTick()

    if (this.filtersReady) {
      await this.listUpdate()
    }

    const filteredInformers = this.mediaToViewscreenArray.filter(item => item.type === 'informer')
    this.informersToViewscreenArray = filteredInformers

    filteredInformers.length === this.informers.length ? this.$emit('changeAllChecked', true) : this.$emit('changeAllChecked', false)
  },
  watch: {
    filtersToBeWatched: {
      handler(newVal, oldVal) {
        if (!this.filtersReady) return

        if (!newVal.search && !oldVal.search && newVal.selectedSearch !== oldVal.selectedSearch) {
          return
        }

        this.listUpdate()
      },
      deep: true,
    },
    isAddAllChecked(newValue) {
      if (newValue) {
        const nonDuplicateIds = this.informers
          .map(informer => informer.context.id)
          .filter(informerId => !this.informersToViewscreenArray.some(item => item.id === informerId))

        this.informersToViewscreenArray.push(
          ...nonDuplicateIds.map(id => ({ id, type: 'informer' })),
        )
      } else {
        this.$emit('mediaToSplice', this.informersToViewscreenArray)
        this.informersToViewscreenArray = []
      }
      this.$emit('changeMediaArray', this.informersToViewscreenArray)
    },
  },
  methods: {
    addToMediaArray(informerId) {
      this.informersToViewscreenArray.push({ id: informerId, type: 'informer' })
      this.$emit('changeMediaArray', this.informersToViewscreenArray)
      if (this.informersToViewscreenArray.length === this.informers.length) {
        this.$emit('changeAllChecked', true)
      }
    },
    deleteFromMediaArray(informerId) {
      const deletedIndex = this.informersToViewscreenArray.findIndex(item => item.id === informerId && item.type === 'informer')

      if (deletedIndex !== -1) {
        this.informersToViewscreenArray.splice(deletedIndex, 1)
        this.$emit('mediaToSplice', informerId, 'informer')
        this.$nextTick(() => {
          this.isInternalCheckboxChange = true
          this.$emit('changeAllChecked', false)
        })
      }
    },
    openInformerPage(id) {
      routerPushWithFilters(this.$router, `/media/informers/edit/${id}`)
    },
    createInformer() {
      routerPushWithFilters(this.$router, '/media/informers/create')
    },
    redirectToMain() {
      this.$router.push(`/main/${this.$route.params.viewscreenId}`)
    },
    openInformerModal(informerObject) {
      this.informerObject = informerObject
      this.isInformerModalOpen = true
    },
    closeInformerModal() {
      this.isInformerModalOpen = false
    },
    async listUpdate() {
      try {
        this.loading = true
        await this.getInformers()
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async getInformers() {
      const { search, active, localizationId, applicationId, selectedSearch } = this.filters
      const searchParams = new URLSearchParams()
      searchParams.append('active', active ? '1' : '0')

      if (typeof localizationId !== 'undefined') {
        searchParams.append('lang_id', localizationId)
      }

      if (typeof applicationId !== 'undefined') {
        searchParams.append('app_id', applicationId)
      }

      if (search !== undefined) {
        searchParams.append(selectedSearch, search)
      }

      if (this.isViewscreen) {
        searchParams.append('section_id', this.$route.params.sectionId)
      }

      try {
        this.loading = true

        const informersResponse = await MediaService.getInformers(searchParams)

        if (!informersResponse?.data?.data) {
          this.$notification.error({
            message: 'Something went wrong. Please try again later.',
          })
        }

        this.informers = informersResponse.data.data
      } catch (err) {
        let errorMessage = 'Something went wrong. Please try again later.'

        if (err.response?.data?.error?.message) {
          errorMessage = err.response.data.error.message
        } else if (err.message) {
          errorMessage = err.message
        }

        this.$notification.error({
          message: errorMessage,
        })
      } finally {
        this.loading = false
      }
    },

    async sortInformers() {
      const informersSort = this.informers.map((informer, index, informerArr) => {
        return {
          id: informer.context.id,
          sort: informerArr.length - index,
        }
      })
      try {
        await MediaService.sortInformers(this.filters.applicationId, informersSort)
        this.$notification.success({
          message: 'Sucessfully sorted',
        })
      } catch (error) {
        console.log(error)
      }
      this.drag = false
    },
  },
}
</script>
